"use client";

import { Calendar, MessageSquareText, MousePointer2, Zap } from "lucide-react";
import { Card } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function TheSensoryExperience() {
  const intro = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const pillars = [{
    icon: Zap,
    title: "Live Pulse Streaming",
    description: "Experience the raw energy of the dance floor in real-time. Our low-latency streams bring the strobe lights and bass directly to your screen.",
    illustration: "cursors"
  }, {
    icon: MessageSquareText,
    title: "VR Immersion",
    description: "Step inside the sanctuary. Use virtual reality eyewear to feel the crowd, the acoustics, and the atmosphere of the world's most exclusive events.",
    illustration: "thread"
  }, {
    icon: Calendar,
    title: "The Event Vault",
    description: "Unlimited access to our curated archive of music concerts, art shows, and sporting events. Relive the indulgence whenever you desire.",
    illustration: "timeline"
  }];
  return <section data-section-id="74" id="the-sensory-experience" className="bg-background text-foreground py-24 md:py-32 lg:py-[140px]">
      <div className="mx-auto w-full max-w-7xl px-6 lg:px-8">
        {}
        <div ref={intro.ref} className={`mx-auto max-w-[720px] text-center transition-all duration-700 ease-out ${intro.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="inline-flex items-center gap-2.5">
            <span className="inline-block size-1.5 rounded-md bg-primary" />
            <span className="font-mono text-xs font-medium uppercase tracking-widest text-primary">
              The Sensory Experience
            </span>
          </div>
          <h2 className="font-default mt-5 text-4xl font-bold leading-[1.05] tracking-tighter text-foreground md:text-5xl lg:text-[52px] uppercase">
            Three pillars of digital indulgence.
          </h2>
          <p className="mt-6 text-base leading-relaxed text-muted-foreground md:text-lg font-default">
            The Church of Over Indulgence unifies the rituals of nightlife and 
            high-fidelity streaming. Enter a world where the event never ends.
          </p>
        </div>

        {}
        <div className="mt-16 grid grid-cols-1 gap-4 sm:grid-cols-2 md:gap-6 lg:mt-[72px] lg:grid-cols-3">
          {pillars.map((pillar, i) => {
          const Icon = pillar.icon;
          return <PillarCard key={pillar.title} data-index={i} index={i} icon={<Icon className="size-6 text-primary" strokeWidth={1.5} />} title={pillar.title} description={pillar.description} illustration={pillar.illustration} lastInOddGrid={i === 2} />;
        })}
        </div>
      </div>
    </section>;
}
function PillarCard({
  index,
  icon,
  title,
  description,
  illustration,
  lastInOddGrid,
  ...rest
}: {
  index: number;
  icon: React.ReactNode;
  title: string;
  description: string;
  illustration: string;
  lastInOddGrid?: boolean;
} & React.HTMLAttributes<HTMLDivElement>) {
  const reveal = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <div {...rest} ref={reveal.ref} className={`group h-full transition-all duration-700 ease-out ${reveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"} ${lastInOddGrid ? "sm:col-span-2 lg:col-span-1" : ""}`} style={{
    transitionDelay: `${index * 120}ms`
  }}>
      <Card className="relative flex h-full flex-col justify-between gap-10 border border-border/20 bg-card p-8 shadow-md transition-all duration-300 ease-out hover:-translate-y-1 hover:shadow-xl hover:border-primary/40 md:p-10 rounded-md">
        {}
        <div>
          <div className="inline-flex size-14 items-center justify-center rounded-md bg-primary/10 transition-colors duration-300 group-hover:bg-primary/20">
            {icon}
          </div>

          <h3 className="font-default mt-6 text-[22px] font-bold leading-tight tracking-tight text-card-foreground uppercase">
            {title}
          </h3>

          <p className="mt-3 text-base leading-relaxed text-muted-foreground font-default">
            {description}
          </p>
        </div>

        {}
        <div className="pt-4">
          {illustration === "cursors" && <CursorsIllustration />}
          {illustration === "thread" && <ThreadIllustration />}
          {illustration === "timeline" && <TimelineIllustration />}
        </div>
      </Card>
    </div>;
}
function CursorsIllustration() {
  return <div className="relative h-16 w-full">
      {}
      <span className="absolute left-3 top-3 h-px w-10 rounded-md bg-primary/40" />
      <span className="absolute left-14 top-7 h-px w-8 rounded-md bg-chart-2/40" />
      <span className="absolute left-28 top-11 h-px w-10 rounded-md bg-chart-3/40" />

      <MousePointer2 className="absolute left-1 top-1 size-6 -rotate-12 text-primary drop-shadow-[0_0_8px_rgba(125,249,255,0.5)]" fill="currentColor" strokeWidth={1} />
      <MousePointer2 className="absolute left-10 top-5 size-6 rotate-6 text-chart-2 drop-shadow-[0_0_8px_rgba(255,79,216,0.5)]" fill="currentColor" strokeWidth={1} />
      <MousePointer2 className="absolute left-24 top-9 size-6 -rotate-3 text-chart-3 drop-shadow-[0_0_8px_rgba(242,215,255,0.5)]" fill="currentColor" strokeWidth={1} />
    </div>;
}
function ThreadIllustration() {
  const items = [{
    color: "bg-primary",
    w: "w-24"
  }, {
    color: "bg-chart-2",
    w: "w-32"
  }, {
    color: "bg-chart-3",
    w: "w-20"
  }];
  return <div className="flex flex-col gap-2">
      {items.map((item, i) => <div key={i} data-index={i} className="flex items-center gap-2.5">
          <span className={`inline-block size-5 shrink-0 rounded-md ${item.color} opacity-80 shadow-[0_0_10px_rgba(0,0,0,0.5)]`} />
          <span className={`block h-2 ${item.w} rounded-md bg-muted/30`} aria-hidden />
        </div>)}
    </div>;
}
function TimelineIllustration() {
  const bars = [{
    color: "bg-primary",
    w: "w-3/4",
    ml: "ml-0"
  }, {
    color: "bg-chart-2",
    w: "w-1/2",
    ml: "ml-8"
  }, {
    color: "bg-chart-3",
    w: "w-2/3",
    ml: "ml-4"
  }];
  return <div className="flex flex-col gap-2">
      {bars.map((bar, i) => <div key={i} data-index={i} className={`${bar.ml}`}>
          <span className={`block h-2.5 ${bar.w} rounded-md ${bar.color} opacity-85 shadow-[0_0_15px_rgba(0,0,0,0.3)]`} />
        </div>)}
    </div>;
}